<template>
    <footer 
        class="footer w-full bg-footer-bg mt-auto"
        :class="{ 'footer-cart-bar-offset': isCartPage }"
        role="contentinfo"
        aria-label="Подвал сайта"
        itemscope
        itemtype="https://schema.org/Organization"
    >
        <div class="footer-inner w-full px-4 py-6 sm:px-6 md:py-7 lg:px-8 lg:py-8 xl:px-10">
            <!-- Первый этаж: О компании -->
            <section 
                class="footer-section-main" 
                id="footer-company-heading"
                aria-labelledby="footer-company-heading"
            >
                <div class="footer-grid-unified">
                    <!-- Логотип -->
                    <div class="footer-logo-cell">
                        <router-link 
                            to="/" 
                            aria-label="Перейти на главную страницу компании КРОН" 
                            itemprop="url"
                        >
                            <img 
                                src="@/assets/big-logo-1x.webp" 
                                srcset="@/assets/big-logo-1x.webp 1x, @/assets/big-logo-2x.webp 2x"
                                sizes="(max-width: 1024px) 176px, 208px"
                                alt="КРОН логотип - оптовый интернет-магазин инструментов и оборудования" 
                                class="brand-logo"
                                width="256"
                                height="37"
                                @error="handleImageError"
                                loading="lazy"
                                itemprop="logo"
                            >
                        </router-link>
                    </div>
                    
                    <!-- Навигация -->
                    <nav class="footer-nav-cell" aria-label="Навигация по сайту">
                        <ul class="footer-nav-tabs" role="list" aria-label="Основная навигация по сайту">
                            <li>
                                <router-link 
                                    to="/" 
                                    class="footer-nav-link-inline"
                                    active-class="footer-nav-link-inline-active"
                                    aria-label="Перейти на главную страницу"
                                >
                                    <span>Главная</span>
                                </router-link>
                            </li>
                            <li>
                                <router-link 
                                    to="/catalog/" 
                                    class="footer-nav-link-inline"
                                    active-class="footer-nav-link-inline-active"
                                    aria-label="Перейти в каталог товаров"
                                >
                                    <span>Каталог</span>
                                </router-link>
                            </li>
                            <li>
                                <router-link 
                                    to="/about" 
                                    class="footer-nav-link-inline"
                                    active-class="footer-nav-link-inline-active"
                                    aria-label="Узнать больше о компании КРОН"
                                >
                                    <span>О компании</span>
                                </router-link>
                            </li>
                            <li>
                                <router-link 
                                    to="/contacts" 
                                    class="footer-nav-link-inline"
                                    active-class="footer-nav-link-inline-active"
                                    aria-label="Посмотреть контактную информацию"
                                >
                                    <span>Контакты</span>
                                </router-link>
                            </li>
                            <li>
                                <router-link 
                                    to="/delivery" 
                                    class="footer-nav-link-inline"
                                    active-class="footer-nav-link-inline-active"
                                    aria-label="Узнать об условиях доставки"
                                >
                                    <span>Доставка</span>
                                </router-link>
                            </li>
                        </ul>
                    </nav>
                </div>
            </section>
            
            <!-- Второй этаж: Контакты -->
            <section 
                class="footer-section-contacts mb-4 md:mb-3 pb-6 md:pb-5 border-b border-slate-200/50" 
                id="footer-contacts-heading"
                aria-labelledby="footer-contacts-heading"
            >
                <h2 id="footer-contacts-heading" class="sr-only">Контактная информация</h2>
                <div class="footer-contacts-column w-full">
                    <div class="footer-region-switcher-wrapper">
                        <FooterRegionSwitcher
                            :regions="regions"
                            :active-region="activeRegionId"
                            @change="handleRegionChange"
                        />
                    </div>

                    <div class="footer-contacts-content w-full">
                        <transition name="fade-slide" mode="out-in">
                            <FooterAddress
                                v-if="activeRegionData"
                                :key="activeRegionId"
                                :region="activeRegionData"
                                :is-active="true"
                            />
                        </transition>
                    </div>
                </div>
            </section>

            <!-- Третий этаж: Правовая информация -->
            <div 
                class="footer-section-legal w-full flex flex-col gap-4 pt-5 md:flex-row md:items-center md:justify-between md:gap-6 md:pt-6"
                role="contentinfo"
                aria-label="Правовая информация и авторские права"
            >
                <p class="m-0 shrink-0 text-center text-xs text-slate-700 md:text-left md:text-sm" role="text">
                    <span aria-label="Авторские права">©</span> 
                    <time :datetime="currentYear.toString()">{{ currentYear }}</time> 
                    <span itemprop="name"> ООО "КРОН"</span>. Все права защищены.
                </p>
                <nav 
                    class="footer-legal-nav flex min-w-0 w-full flex-wrap items-center justify-center gap-x-4 gap-y-2 md:w-auto md:justify-end lg:gap-x-6" 
                    aria-label="Правовая информация"
                    role="navigation"
                >
                    <router-link 
                        to="/policy" 
                        class="footer-legal-link text-xs md:text-sm text-slate-700"
                        aria-label="Политика обработки персональных данных и конфиденциальности"
                    >
                        Политика конфиденциальности
                    </router-link>
                    <router-link 
                        to="/offer" 
                        class="footer-legal-link text-xs md:text-sm text-slate-700"
                        aria-label="Публичная оферта"
                    >
                        Публичная оферта
                    </router-link>
                    <router-link 
                        to="/terms" 
                        class="footer-legal-link text-xs md:text-sm text-slate-700"
                        aria-label="Пользовательское соглашение"
                    >
                        Пользовательское соглашение
                    </router-link>
                    <router-link 
                        to="/returns" 
                        class="footer-legal-link text-xs md:text-sm text-slate-700"
                        aria-label="Возврат и обмен товара"
                    >
                        Возврат и обмен
                    </router-link>
                </nav>
            </div>
        </div>
    </footer>
</template>

<script>
import { useAuth } from '@/store/useAuth'
import FooterAddress from './FooterAddress.vue'
import FooterRegionSwitcher from './FooterRegionSwitcher.vue'
import { logWarn } from '@/utils/logger'
import { handleImageError as setFallbackImage } from '@/utils/imageUtils'

export default {
  name: 'MainFooter',
  components: {
    FooterAddress,
    FooterRegionSwitcher
  },
  setup() {
    const authInfo = useAuth()
    return { authInfo }
  },
  data() {
    return {
      fallbackRegionId: 'moscow',
      selectedRegionId: null,
      regions: [
        {
          id: 'moscow',
          name: 'Москва и МО',
          aliases: ['Москва и МО', 'Москва', 'Московская область', 'Москва и мо'],
          phone: '+7 (495) 799-26-66',
          phoneHref: 'tel:+74957992666',
          email: 'info@tovari-kron.ru',
          emailHref: 'mailto:info@tovari-kron.ru',
          address: 'Домодедовское шоссе, 4-й километр, 15Б',
          mapUrl: 'https://yandex.ru/maps/org/tovary_kron/11954850843/?ll=37.662964%2C55.438711&z=7',
          mapEmbedUrl: 'https://yandex.ru/map-widget/v1/?ll=37.662964,55.438711&z=13&pt=37.662964,55.438711,pm2rdm&l=map',
          timezone: 'Europe/Moscow', // UTC+3
          workingHours: {
            weekdays: 'Понедельник – Суббота: 9:00 – 19:00',
            weekend: 'Воскресенье: выходной день'
          }
        },
        {
          id: 'stavropol',
          name: 'Ставропольский край',
          aliases: ['Ставропольский край', 'Ставрополь'],
          phone: '+7 (903) 418-16-66',
          phoneHref: 'tel:+79034181666',
          email: 'ug@tovari-kron.ru',
          emailHref: 'mailto:ug@tovari-kron.ru',
          address: 'с. Надежда, ул. Орджоникидзе 79',
          mapUrl: 'https://yandex.ru/maps/?pt=42.080726,45.039356&z=16',
          mapEmbedUrl: 'https://yandex.ru/map-widget/v1/?ll=42.080726,45.039356&z=15&pt=42.080726,45.039356,pm2rdm&l=map',
          timezone: 'Europe/Moscow', // UTC+3
          workingHours: {
            weekdays: 'Понедельник – Суббота: 9:00 – 19:00',
            weekend: 'Воскресенье: выходной день'
          }
        }
      ]
    }
  },
  computed: {
    isCartPage() {
      return this.$route.path === '/cart'
    },
    currentYear() {
      return new Date().getFullYear()
    },
    activeRegionId() {
      return this.selectedRegionId || this.fallbackRegionId
    },
    activeRegionData() {
      const found = this.regions.find(r => r.id === this.activeRegionId)
      if (found) {
        return found
      }
      return this.regions && this.regions.length > 0 ? this.regions[0] : null
    }
  },
  methods: {
    handleRegionChange(regionId) {
      this.selectedRegionId = regionId
    },
    normalizeRegionName(name) {
      return (name || '').toString().trim().toLowerCase().replace(/\s+/g, ' ')
    },
    syncSelectedRegionWithAuth() {
      const authRegionName = this.normalizeRegionName(this.authInfo.region?.name)
      if (!authRegionName) {
        this.selectedRegionId = this.fallbackRegionId
        return
      }

      const matchedRegion = this.regions.find((region) => {
        if (region.id === this.authInfo.region?.id) {
          return true
        }
        const aliases = region.aliases || []
        return aliases.some(alias => this.normalizeRegionName(alias) === authRegionName)
      })

      if (matchedRegion) {
        this.selectedRegionId = matchedRegion.id
      } else if (!this.selectedRegionId) {
        this.selectedRegionId = this.fallbackRegionId
      }
    },
    handleImageError(event) {
      // Fallback для логотипа при ошибке загрузки
      logWarn('Failed to load footer logo image')
      setFallbackImage(event)
      event.target.style.display = 'none'
    }
  },
  created() {
    this.syncSelectedRegionWithAuth()
  },
  watch: {
    'authInfo.region.name': {
      handler() {
        this.syncSelectedRegionWithAuth()
      }
    }
  }
}
</script>

<style scoped>
/* Плавная анимация переключения адресов */
.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}

.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

.fade-slide-enter-to,
.fade-slide-leave-from {
  opacity: 1;
  transform: translateY(0);
}

/* ===== FOOTER STRUCTURE STYLES ===== */

/* ===== FOOTER TYPOGRAPHY & STRUCTURE ===== */

/* Заголовки секций - единый стиль */
.footer-section-title {
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #475569;
  margin: 0;
  display: inline-flex;
  align-items: center;
  position: relative;
  padding-bottom: 4px;
}

.footer-section-title--underline::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, rgba(148, 163, 184, 0.4) 0%, rgba(148, 163, 184, 0.9) 100%);
  border-radius: 9999px;
}

.footer-section-title--accent {
  color: var(--color-brand, #DC2626);
}

.footer-section-title--accent::after {
  background: linear-gradient(90deg, rgba(220, 38, 38, 0.85) 0%, rgba(248, 113, 113, 0.8) 100%);
}

/* Секции футера - сбалансированные отступы */
.footer-section-main {
  padding-bottom: 0;
  margin-bottom: 0;
  padding-top: 0;
  margin-top: 0;
  min-height: auto;
}

.footer-section-contacts {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
}

.footer-section-legal {
  padding-top: 0;
}

.footer-legal-nav {
  min-width: 0;
}

.footer-legal-nav .footer-legal-link {
  white-space: normal;
  word-break: break-word;
}

/* На мобилке на странице корзины плашка «Оформить» фиксирована снизу — добавляем отступ, чтобы футер не перекрывался */
@media (max-width: 1023px) {
  .footer.footer-cart-bar-offset {
    padding-bottom: calc(5.5rem + env(safe-area-inset-bottom, 0));
  }
}

/* ===== FOOTER SHELL ===== */

.footer-inner {
  max-width: none;
}

/* ===== FOOTER GRID SYSTEM ===== */

.footer-grid-unified {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  align-items: stretch;
  width: 100%;
}

@media (min-width: 768px) {
  .footer-grid-unified {
    flex-direction: row;
    align-items: center;
    gap: 2rem;
  }
}

@media (min-width: 1280px) {
  .footer-grid-unified {
    gap: 3rem;
  }
}

.footer-logo-cell {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-shrink: 0;
  width: 100%;
  max-width: 208px;
}

.footer-logo-cell router-link,
.footer-logo-cell a {
  display: inline-block;
  margin: 0;
  padding: 0;
  width: 100%;
}

.footer-logo-cell img {
  height: auto;
  display: block;
}

.footer-nav-cell {
  display: flex;
  flex: 1;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  gap: 0.5rem;
  justify-content: center;
}

.footer-contacts-column {
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
  padding-top: 0.9rem;
}

.footer-contacts-column::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: linear-gradient(90deg, rgba(148, 163, 184, 0.15) 0%, rgba(148, 163, 184, 0.4) 50%, rgba(148, 163, 184, 0.15) 100%);
  border-radius: 9999px;
}

.footer-region-switcher-wrapper {
  display: flex;
  justify-content: flex-start;
  width: 100%;
  margin-bottom: 1rem;
}

@media (min-width: 768px) {
  .footer-region-switcher-wrapper {
    justify-content: flex-end;
    margin-bottom: 1.25rem;
  }
}

/* Убрали :deep() - используем CSS переменную для общения с ребенком */
.footer-region-switcher-wrapper {
  --region-switcher-margin: 0;
}

/* Контакты - строго по сетке без дополнительных оберток */
.footer-contacts-content {
  width: 100%;
  min-height: 0;
}

/* ===== FOOTER NAVIGATION STYLES ===== */

/* Список навигации - убираем стили списка */
.footer-nav-tabs {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 0.625rem 1rem;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
}

@media (min-width: 768px) {
  .footer-nav-tabs {
    justify-content: flex-end;
    gap: 0.75rem 1.25rem;
  }
}

@media (min-width: 1024px) {
  .footer-nav-tabs {
    justify-content: flex-end;
    gap: 0.75rem 1.5rem;
  }
}

.footer-nav-tabs li {
  display: inline-block;
  margin: 0;
  padding: 0;
}

/* Текстовые ссылки навигации без «таблеток» */
.footer-nav-link-inline {
  display: inline;
  padding: 0;
  border: 0;
  border-radius: 0;
  text-decoration: none;
  font-size: 0.8125rem;
  font-weight: 500;
  line-height: 1.35;
  color: #475569;
  background: transparent;
  white-space: nowrap;
  transition: color 0.2s ease;
}

@media (min-width: 768px) {
  .footer-nav-link-inline {
    font-size: 0.875rem;
  }
}

.footer-nav-link-inline:hover {
  color: #dc2626;
  background: transparent;
  box-shadow: none;
  transform: none;
}

.footer-nav-link-inline-active {
  color: #dc2626;
  background: transparent;
  border: 0;
  font-weight: 600;
  box-shadow: none;
}

/* Правовые ссылки внизу футера */
.footer-legal-link {
  color: #334155;
  text-decoration: none;
  transition: all 0.2s ease;
  padding: 4px 0;
  position: relative;
}

.footer-legal-link::after {
  content: '';
  position: absolute;
  bottom: 2px;
  left: 0;
  width: 0;
  height: 1px;
  background: var(--color-brand, #DC2626);
  transition: width 0.2s ease;
}

.footer-legal-link:hover {
  color: var(--color-brand, #DC2626);
}

.footer-legal-link:hover::after {
  width: 100%;
}
</style>